import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";

const EmployeeChart = ({ data, emps, selectedJob, setSelectedJob }) => {
  const { t } = useTranslation();
  // ✅ Extract jobs from "data"
  const jobs = useMemo(
    () =>
      Array.isArray(data)
        ? data.map((job) => ({
            jobId: job.jobId ?? job.id,
            jobName: job.jobName,
          }))
        : [],
    [data]
  );

  // ✅ Default job

  // ✅ Filter & prepare Recharts data
  const filteredEmployees = useMemo(() => {
    return (emps || [])
      .filter(
        (e) =>
          e &&
          typeof e.employeeName === "string" &&
          typeof e.score === "number" &&
          !isNaN(e.score) &&
          (!selectedJob || e.jobId === selectedJob)
      )
      .sort((a, b) => a.score - b.score)
      .map((e) => ({
        employeeName: e.employeeName,
        score: e.score,
        color: e.score >= 90 ? "#66bb6a" : e.score >= 70 ? "#ffa726" : "#ef5350",
        job: jobs.find((j) => j.jobId === e.jobId)?.jobName || "Unknown Job",
      }));
  }, [emps, selectedJob, jobs]);

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {t("user.manpowerPlanningAnalysis.chart.employeeScore")}
          </Typography>

          {/* ✅ Job Dropdown */}
          <Autocomplete
            options={[{ jobId: null, jobName: t("admin.employees.filters.allJobs") }, ...jobs]}
            getOptionLabel={(option) => option.jobName || ""}
            value={
              jobs.find((job) => job.jobId === selectedJob) || {
                jobId: null,
                jobName: t("admin.employees.filters.allJobs"),
              }
            }
            onChange={(_, newValue) => setSelectedJob(newValue?.jobId || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("user.hoursCalculator.selectJob")}
                variant="outlined"
                sx={{ ...fieldStyles, width: "30%" }}
              />
            )}
          />

          {/* ✅ Chart */}
          {filteredEmployees.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={filteredEmployees}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="employeeName" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "10px 12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <p style={{ margin: 0, fontWeight: "bold", color: "#3f51b5" }}>
                            {data.employeeName}
                          </p>
                          <p style={{ margin: "4px 0 0", color: "#333" }}>
                            <strong>{t("admin.manpowerRequirement.score")}:</strong> {data.score}%
                          </p>
                          <p style={{ margin: "2px 0 0", color: "#555" }}>
                            <strong>{t("admin.manpowerRequirement.job")}:</strong> {data.job || "-"}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />

                <Bar dataKey="score" name="Score" radius={6}>
                  {filteredEmployees.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No employees found for this job.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

EmployeeChart.propTypes = {
  data: PropTypes.array.isRequired,
  emps: PropTypes.array.isRequired,
  selectedJob: PropTypes.any.isRequired,
  setSelectedJob: PropTypes.func.isRequired,
};

export default EmployeeChart;
